import * as React from 'react'
import {useEffect, useState} from 'react'
import {Button, Col, DatePicker, Form, Radio, Row, Table, Typography} from 'antd';
import DoughnutChartByPublisher from './DoughnutChartByPublisher';
import {addCommas, beautifyReportData, DateOption, roundBySlice, useWindowSize, WindowSize} from '../../../../../util/Util';
import moment from 'dayjs';
import {
    KeyFilterClause,
    PublisherReportKeyColumnDescription,
    PublisherReportValueColumnDescription,
} from '../../../../../api/api';
import reportApi from '../../../reports/ReportApi';
import PieChartByPublisher from './PieChartByPublisher';
import BarChartByPublisher from './BarChartByPublisher';
import './style.css'
import {ColumnsType} from 'antd/lib/table';
import {env} from '../../../../../env';

const {Text} = Typography;
const {RangePicker} = DatePicker;

const FormItem = Form.Item
const dateFormat = 'YYYY-MM-DD'
const PublisherKeyColumns = PublisherReportKeyColumnDescription.ColumnEnum
const PublisherValueColumns = PublisherReportValueColumnDescription.ColumnEnum
let pubDat: PublisherData[] = [];

export interface PublisherData {
    id: number;
    name: string;
    revenue: string;
    request: string;
    win: string;
    impression: string;
    view: string;
    click: string;
    vastStart: string;
    vastComplete: string;
}

export const AdminDashboardByPublisher = () => {
    const windowSize: WindowSize = useWindowSize();

    interface DataType {
        key: React.ReactNode;
        publisher: string;
        impressionsCount: string;
        winsCount: string;
        revenue: string;
        requests: string;
        children?: DataType[];
    }

    const [datePeriod, setDatePeriod] = useState<any>(DateOption.Month)
    const [dates, setDates] = useState<[moment.Dayjs, moment.Dayjs]>([moment(moment().subtract(1, 'months'), dateFormat), moment(moment(), dateFormat)])
    const [form] = Form.useForm()

    const [loading, setLoading] = useState(false)
    const [revenueChart, setRevenueChart] = useState(true)
    const [eventsChart, setEventsChart] = useState(false)
    const [selectedRow, setSelectedRow] = useState()
    const [hideDiagram, setHideDiagram] = useState(false)
    const [tableData, setTableData] = useState<DataType[]>([])
    const [colSpan, setColSpan] = useState(window.innerWidth < 1850 ? 12 : 8);
    const columns: ColumnsType<DataType> = [
        {
            title: 'Publisher',
            dataIndex: 'publisher',
            width: '25%',
            key: 'publisher',
            render: (value) => {
                return (
                    <a
                        // onClick={(event) => rowSelect(event)}
                        // href={value}
                        // target="_blank"
                    >
                        {value}
                    </a>
                );
            }
        },
        {
            title: 'Revenue',
            dataIndex: 'revenue',
            width: '21%',
            key: 'revenue',
        },
        {
            title: 'Requests',
            dataIndex: 'requests',
            width: '18%',
            key: 'requests',
        },
        {
            title: 'Wins',
            dataIndex: 'winsCount',
            width: '18%',
            key: 'winsCount',
        },
        {
            title: 'Imp',
            dataIndex: 'impressionsCount',
            key: 'impressionsCount',
            width: '18%',
        }
    ]

    function prepareAndSetData(rowsData?: string[][]) {
        let tData: DataType[] = []

        rowsData?.forEach((row, idx) => {
            tData.push({
                key: idx,
                publisher: row[0],
                revenue: row[1],
                requests: row[2],
                winsCount: row[3],
                impressionsCount: row[4]
            })
        });

        setTableData(tData)
    }

    const loadData = async () => {
        setLoading(true)
        let dateCol: PublisherReportKeyColumnDescription
        dateCol = {
            column: PublisherKeyColumns.Date,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: dates?.map(d => d.format(dateFormat))
            }
        }
        dateCol.onlyFilter = true
        const keyCols: PublisherReportKeyColumnDescription[] = []
        keyCols.push({column: PublisherKeyColumns.PublisherId})
        keyCols.push({column: PublisherKeyColumns.Publisher})
        keyCols.push(dateCol)
        keyCols.push({
            column: PublisherKeyColumns.DspId,
            where: {
                type: KeyFilterClause.TypeEnum.Equals,
                value: 0
            },
            onlyFilter: true
        })
        const valueCols: PublisherReportValueColumnDescription[] = []
        valueCols.push({column: PublisherValueColumns.RevenueWin})
        valueCols.push({column: PublisherValueColumns.Requests})
        valueCols.push({column: PublisherValueColumns.Wins})
        valueCols.push({column: PublisherValueColumns.Impressions})
        valueCols.push({column: PublisherValueColumns.View})
        valueCols.push({column: PublisherValueColumns.Click})
        valueCols.push({column: PublisherValueColumns.VastStart})
        valueCols.push({column: PublisherValueColumns.VastComplete})

        const uuid = await reportApi.createPublisher({
            keyColumns: keyCols,
            valueColumns: valueCols
        }).then(resp => resp.data)
        if (!uuid) {
            return
        }
        let interval = setInterval(async () => {

            try {
                const response = await reportApi.get(uuid ?? '');
                clearInterval(interval);
                const blob = await response.blob();
                const text = await blob.text();
                if (text.length < 1) {
                    return;
                }
                const rows = text.split('\n');
                if (rows.length < 2) {
                    return;
                }
                let header = rows[0].split(',');
                rows.shift();
                rows.pop();
                let rowsData = rows.map(row => row.split(','));
                pubDat = []

                // Filter and map data, excluding publishers with specified IDs
                rowsData.forEach(d => {
                    const publisherId = parseInt(d[0]);
                    if (!env.excludedPublisherIds.includes(publisherId)) {
                        pubDat.push({
                            id: publisherId,
                            name: d[1],
                            revenue: roundBySlice(d[2]),
                            request: d[3],
                            win: d[4],
                            impression: d[5],
                            view: d[6],
                            click: d[7],
                            vastStart: d[8],
                            vastComplete: d[9]
                        });
                    }
                });

                // Filter rowsData for table display
                const filteredRowsData = rowsData.filter(d => {
                    const publisherId = parseInt(d[0]);
                    return !env.excludedPublisherIds.includes(publisherId);
                });

                // Remove PublisherId column from rowsData for table display (shift indices)
                const tableRowsData = filteredRowsData.map(row => row.slice(1));

                beautifyReportData(header, tableRowsData);
                prepareAndSetData(tableRowsData);
                setLoading(false);
            } catch (response) {
                if (typeof response === 'object') {
                    if (response.status !== 425) {
                        setLoading(false);
                        clearInterval(interval);
                    }
                }
            }
        }, 500)
    }

    const initialLoad = () => {
        loadData()
    }

    useEffect(() => {
        initialLoad()
    }, [])

    useEffect(() => {
        if (windowSize.width && windowSize.width < 1850) {
            setHideDiagram(true);
            setColSpan(12)
        } else {
            setHideDiagram(false)
            setColSpan(8)
        }
    }, [windowSize])

    const handleDatePeriod = (e) => {
        setDatePeriod(e.target.value)
        setDates([moment(moment().subtract(1, e.target.value), dateFormat), moment(moment(), dateFormat)])
    }

    const handleDates = (e) => {
        setDatePeriod(undefined)
        setDates(e)
    }

    const rowSelect = (e) => {
        setSelectedRow(e ? e.publisher : undefined)
    }

    function selectChart(e) {
        setRevenueChart(false)
        setEventsChart(false)

        if (e.target.value === 'revenue') {
            setRevenueChart(true)
        } else {
            setEventsChart(true)
        }
    }

    return (
        <div>
            <Row wrap={false}>
                <Col span={colSpan} hidden={hideDiagram && !revenueChart}>
                    <PieChartByPublisher style={{
                        height: '415px'
                    }}
                                         pubDat={pubDat}
                    />
                    <Col>
                        <DoughnutChartByPublisher style={{
                            height: '415px'
                        }}
                                                  pubDat={pubDat}
                        />
                    </Col>
                </Col>
                <Col span={colSpan} hidden={hideDiagram && !eventsChart}>
                    <BarChartByPublisher style={{
                        height: '850px'
                    }}
                                         pubDat={pubDat}
                                         publisher={selectedRow}
                    />
                </Col>
                <Col span={colSpan}>
                    <Form
                        style={{
                            maxWidth: '1000px',
                            alignContent: 'center'
                        }}
                        onFinish={loadData}
                        form={form}
                        labelCol={{span: 1}}
                        wrapperCol={{span: 21}}
                    >
                        <FormItem style={{margin: '0px 0px -10px 10px'}}>
                            <Radio.Group id={'radioButtonsWithoutLines'} value={datePeriod} onChange={handleDatePeriod}>
                                {Object.keys(DateOption).map(option => <Radio.Button
                                    style={{border: '0px'}}
                                    key={option}
                                    value={option}>{option}</Radio.Button>)}
                            </Radio.Group>
                        </FormItem>

                        <FormItem
                            rules={[
                                {
                                    required: true,
                                    message: 'Please choose dates'
                                }
                            ]}
                        >
                            <RangePicker value={dates} onChange={handleDates}
                                         style={{width: '280px', margin: '0 0 0 10px'}} format="YYYY-MM-DD"/>
                            <Button style={{
                                backgroundColor: '#657dcb', margin: '10px 10px 0 10px'
                            }} type="primary" htmlType="submit" loading={loading}>Apply</Button>
                        </FormItem>
                    </Form>
                    <Radio.Group defaultValue="revenue" size="small" style={{ margin: '0px 0px 5px 10px' }} onChange={selectChart}>
                        <Radio.Button value="revenue"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Revenue</Radio.Button>
                        <Radio.Button value="events"><i className="topbar__dropdownMenuIcon icmn-stats-bars"/>Events</Radio.Button>
                    </Radio.Group>
                    <Table
                        size={'middle'}
                        loading={loading}
                        id={'dashboardTable'}
                        scroll={{y: 700}}
                        bordered={true}
                        pagination={false}
                        columns={columns}
                        onRow={(record, rowIndex) => {
                            return {
                                onClick: () => {
                                    rowSelect(record)
                                },
                            };
                        }}
                        dataSource={tableData}

                        summary={pageData => {
                            let totalRevenue = 0;
                            let totalRequests = 0;
                            let totalWins = 0;
                            let totalImpressions = 0;

                            pageData.forEach(({revenue, requests, winsCount, impressionsCount}) => {
                                totalRevenue += Number.parseFloat(revenue.replace(/[$\s,]/g, ''));
                                totalRequests += Number.parseFloat(requests.replace(',', ''));
                                totalWins += Number.parseFloat(winsCount.replace(',', ''));
                                totalImpressions += Number.parseFloat(impressionsCount.replace(',', ''));
                            });

                            return (
                                <>
                                    <Table.Summary.Row style={{backgroundColor: 'lightgrey'}}>
                                        <Table.Summary.Cell index={0}>
                                            <a style={{display: 'flex'}}
                                            onClick={() => rowSelect(undefined)}>Total</a></Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                            <Text>{'$ ' + addCommas(totalRevenue.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                            <Text>{addCommas(totalRequests.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3}>
                                            <Text>{addCommas(totalWins.toString())}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={4}>
                                            <Text>{addCommas(totalImpressions.toString())}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </>
                            );
                        }}
                    />
                </Col>
            </Row>
        </div>
    )
}
